import React, { useEffect } from "react";
import styled, { css } from "styled-components";
import { useResponsive } from "../../hooks/useResponsive";

export type ToastType = "success" | "error";

interface ToastProps {
    message: string;
    type?: ToastType;
    duration?: number; // ms
    onClose?: () => void;
}

export const Toast: React.FC<ToastProps> = ({ message, type = "success", duration = 4000, onClose }) => {
    const { isMobile, isTablet } = useResponsive();

    useEffect(() => {
        if (!duration) return;
        const timer = setTimeout(() => {
            onClose?.();
        }, duration);
        return () => clearTimeout(timer);
    }, [duration, onClose]);

    return (
        <ToastContainer $type={type} $isMobile={isMobile} $isTablet={isTablet}>
            <span>{message}</span>
            <CloseButton onClick={onClose} $isMobile={isMobile}>&times;</CloseButton>
        </ToastContainer>
    );
};

const ToastContainer = styled.div<{ $type: ToastType; $isMobile?: boolean; $isTablet?: boolean }>`
  position: fixed;
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #fff;
  box-shadow: 0 2px 12px rgba(0,0,0,0.15);
  background: ${({ $type }) =>
        $type === "success"
            ? "#2ecc40"
            : $type === "error"
                ? "#e74c3c"
                : "#333"};
  animation: fadeIn 0.3s;

  /* Desktop styling */
  top: 32px;
  right: 32px;
  min-width: 240px;
  max-width: 360px;
  padding: 16px 24px;
  border-radius: 8px;
  font-size: 16px;
  gap: 16px;

  /* Tablet adjustments */
  ${({ $isTablet }) =>
        $isTablet &&
        css`
      top: 20px;
      right: 20px;
      min-width: 220px;
      max-width: 320px;
      padding: 14px 20px;
      font-size: 15px;
      gap: 14px;
    `}

  /* Mobile adjustments */
  ${({ $isMobile }) =>
        $isMobile &&
        css`
      top: 16px;
      left: 16px;
      right: 16px;
      min-width: unset;
      max-width: unset;
      width: calc(100% - 32px);
      padding: 12px 16px;
      border-radius: 6px;
      font-size: 14px;
      gap: 12px;
      
      /* Center horizontally on mobile */
      transform: translateX(0);
      
      /* Ensure text wraps properly on mobile */
      word-wrap: break-word;
      hyphens: auto;
    `}

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-16px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const CloseButton = styled.button<{ $isMobile?: boolean }>`
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  flex-shrink: 0;
  
  /* Desktop styling */
  font-size: 20px;
  margin-left: 8px;
  
  /* Mobile adjustments */
  ${({ $isMobile }) =>
        $isMobile &&
        css`
      font-size: 18px;
      margin-left: 4px;
      padding: 4px;
      border-radius: 2px;
      
      /* Slightly larger touch target on mobile */
      min-width: 24px;
      min-height: 24px;
      
      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }
    `}
  
  &:hover {
    opacity: 0.8;
  }
  
  &:active {
    opacity: 0.6;
  }
`; 